import { FC, ReactNode, useMemo, useState } from 'react';

import { EventInterestOption, EventRsvpOption, PublicFeedEvent } from '@shared/types/event';
import {
  PublicUserConnectionComparison,
  PublicUserConnectionStatus,
  ScheduleDateDetailsEvent,
  UserConnectionStatus,
} from '@shared/types/userConnection';
import { capitalize, orderBy, uniqBy } from 'lodash';
import pluralize from 'pluralize';
import { GATHERING_TERM_PLURAL } from 'src/constants/Connections';

import {
  UserEventRsvp,
  useCurrentUserEventRsvpsQuery,
  useUserEventsQuery,
} from '@queries/EventQueries';
import {
  useUserConnectionComparisonQuery,
  useUserConnectionsQuery,
} from '@queries/UserConnectionsQueries';
import { useCurrentUser } from '@queries/UserQueries';

import { useRsvpToEventMutation } from '@mutations/EventMutations';
import {
  useInviteConnectionMutation,
  useSendConnectionRequest,
} from '@mutations/UserConnectionMutations';

import { useAnalytics } from '@hooks/useAnalytics';

import { useManagedHistory, useNavigate } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button, { ButtonProps } from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { ShareLinkButton } from '@components/buttons/ShareLinkButton';
import { EventHeader } from '@components/cards/EventCard';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { LineAwesomeIcon } from '@components/icons/LineAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { CopyText } from '@components/layout/CopyText/CopyText';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { PopoverWrapper } from '@components/layout/Popover/PopoverWrapper';
import { PrimaryCtaCard } from '@components/layout/PrimaryCtaCard/PrimaryCtaCard';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { UserAvatar, UserAvatarProps } from '@components/layout/UserAvatar/UserAvatar';
import { UserOnboardingGatePopup } from '@components/overlays/UserOnboardingGatePopup';

import { useAuthGate } from '@screens/LogInScreen/AuthGateContext';
import prideSvgUrl from '@screens/entertainer/EntertainerDetailsScreen/assets/pride.svg';
import { useEventRsvpActions } from '@screens/event/EventRsvpScreen/hooks/useEventRsvpActions';
import { DenverPride2026PrimaryCtaCard } from '@screens/series/components/DenverPride2026PrimaryCtaCard';

import { useCurrentUserEventRsvp } from '../../queries/EventQueries';
import { SuggestedConnectionCard } from './SuggestedConnectionsScreen/SuggestedConnectionsScreen';

const CurrentUserEventRsvp: FC<{
  rsvp: EventRsvpOption | EventInterestOption | undefined;
  onUpdateRsvp: (updatedRsvp: EventRsvpOption) => void;
}> = ({ rsvp, onUpdateRsvp }) => {
  const buttonProps = useMemo<ButtonProps>(() => {
    if (rsvp === 'yes') {
      return { children: 'Going', color: 'teal' };
    }
    if (rsvp === 'maybe') {
      return { children: 'Maybe', color: 'teal', variant: 'smooth' };
    }
    if (rsvp === 'no') {
      return { children: "Can't go", color: 'danger', variant: 'smooth' };
    }
    if (rsvp === 'interested') {
      return { children: 'Interested', color: 'yellow', variant: 'smooth' };
    }
    return {
      children: 'No answer',
      color: 'neutral',
      variant: 'smooth',
    };
  }, [rsvp]);

  const Wrapper = onUpdateRsvp ? PopoverWrapper : 'div';

  return (
    <Wrapper
      PopoverContent={({ onClose }) => (
        <div className="py-1">
          <Button
            roundness="rounded"
            variant="flat"
            color="neutral"
            onClick={() => {
              onUpdateRsvp?.('yes');
              onClose();
            }}
          >
            Going
          </Button>
          <Button
            roundness="rounded"
            variant="flat"
            color="neutral"
            onClick={() => {
              onUpdateRsvp?.('maybe');
              onClose();
            }}
          >
            Maybe
          </Button>
          <Button
            roundness="rounded"
            variant="flat"
            color="neutral"
            onClick={() => {
              onUpdateRsvp?.('no');
              onClose();
            }}
          >
            Can't go
          </Button>
        </div>
      )}
    >
      <Button
        {...buttonProps}
        size="sm"
        addBorder
        endIcon={
          onUpdateRsvp ? (
            <FontAwesomeIcon name="chevron-down" style={{ transform: 'translateY(1px)' }} />
          ) : undefined
        }
        style={{ fontSize: '.7rem' }}
      />
    </Wrapper>
  );
};

const UserRsvp: FC<{
  title: string;
  rsvp: EventRsvpOption | EventInterestOption | undefined;
  footer?: ReactNode;
  onUpdateRsvp?: (updatedRsvp: EventRsvpOption) => void;
}> = ({ title, rsvp, footer, onUpdateRsvp }) => {
  return (
    <div
      className="d-flex flex-column h-100 p-3"
      style={{ borderRadius: 8, background: 'rgba(0, 0, 0, 0.3)' }}
    >
      <div className="mb-2 flex-grow-1">
        <strong>{title}</strong>
      </div>
      <div className="flex-grow-0">
        <CurrentUserEventRsvp rsvp={rsvp} onUpdateRsvp={onUpdateRsvp} />
      </div>
      {footer && <div>{footer}</div>}
    </div>
  );
};

const EventComparisonCard: FC<{
  isCurrentUser: boolean;
  event: PublicFeedEvent;
  connectionRsvp: { rsvp: EventRsvpOption | 'interested' } | undefined;
  connectionComparison: PublicUserConnectionComparison;
}> = ({ isCurrentUser, event, connectionRsvp, connectionComparison }) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  const [updatedRsvp, setUpdatedRsvp] = useState<EventRsvpOption>();
  const { rsvp: currentUserRsvp } = useCurrentUserEventRsvp(event._id);
  const { rsvpToEventAsync } = useRsvpToEventMutation();
  const { isInvitingConnection, inviteConnectionAsync } = useInviteConnectionMutation();
  const [didInviteConnection, setDidInviteConnection] = useState(false);
  const { createAuthGateHandler } = useAuthGate();
  const { isSendingConnectionRequest, sendConnectionRequestAsync } = useSendConnectionRequest();

  const handleInviteConnection = async () => {
    track('Invite connection to event', {
      userId: connectionComparison.connectionUserId,
      eventId: event._id,
    });

    await inviteConnectionAsync({
      otherUserId: connectionComparison.connectionUserId,
      eventId: event._id,
    });

    setDidInviteConnection(true);
  };

  const buttons: { key: string; isVisible: boolean; buttonEl: ReactNode }[] = [
    {
      key: 'invite',
      isVisible:
        !isCurrentUser &&
        !['yes', 'maybe', 'no'].includes(connectionRsvp?.rsvp as string) &&
        connectionComparison.status === PublicUserConnectionStatus.Accepted,
      buttonEl: (
        <Button
          roundness="rounded"
          color="neutral"
          variant="smooth"
          startIcon={<FontAwesomeIcon name="paper-plane" />}
          disabled={isInvitingConnection || didInviteConnection}
          onClick={handleInviteConnection}
        >
          {didInviteConnection ? 'Invite Sent!' : `Invite ${connectionComparison.connectionName}`}
        </Button>
      ),
    },
  ].filter(({ isVisible }) => isVisible);

  const handleUpdateRsvp = async (rsvp: EventRsvpOption) => {
    track('Update RSVP', {
      eventId: event._id,
      rsvp,
    });

    createAuthGateHandler(
      async () => {
        setUpdatedRsvp(rsvp);

        await rsvpToEventAsync({
          eventId: event._id,
          rsvp,
          referredBy: connectionComparison.connectionUserId,
        });

        await sendConnectionRequestAsync({
          toUserId: connectionComparison.connectionUserId,
        });
      },
      {
        authTitle: `RSVP to ${event.title ?? 'this event'}`,
        authMessage: `Sign in to let ${connectionComparison.connectionName} know if you're going to this event.`,
      },
    );
  };

  const handleViewEvent = () => {
    navigate(`/events/${event._id}/rsvp`);
  };

  const IMAGE_SIZE = 100;

  return (
    <Card key={event._id}>
      <Card.Body>
        <div
          className="d-flex align-items-center gap-3 mb-3"
          role="button"
          onClick={handleViewEvent}
        >
          <div className="d-flex align-items-center flex-shrink-0">
            <img
              src={event.img}
              alt={event.title}
              style={{
                width: IMAGE_SIZE,
                height: IMAGE_SIZE,
                objectFit: 'cover',
                borderRadius: 8,
              }}
            />
          </div>
          <div className="d-flex justify-content-between w-100">
            <div className="py-2 w-100">
              <div
                className="d-flex gap-1 justify-content-between align-items-center mb-1"
                style={{ lineHeight: 1 }}
              >
                <div>
                  <strong>{event.title}</strong>
                </div>
                <div className="ms-auto">
                  <IconButton color="neutral" variant="smooth">
                    <FontAwesomeIcon name="chevron-right" />
                  </IconButton>
                </div>
              </div>
              <div style={{ fontSize: '.7rem', opacity: 0.8 }}>
                <div>
                  <span className="me-1">
                    <LineAwesomeIcon name="clock" />
                  </span>
                  {event.start}
                </div>
                <div>
                  <span className="me-1">
                    <LineAwesomeIcon name="map-marker" />
                  </span>
                  {event.venue.title}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="d-flex w-100 gap-3">
            <div className={isCurrentUser ? 'w-100' : 'w-50'}>
              <UserRsvp
                title="You"
                rsvp={updatedRsvp ?? currentUserRsvp?.rsvp ?? currentUserRsvp?.eventInterest}
                onUpdateRsvp={handleUpdateRsvp}
              />
            </div>
            {!isCurrentUser && (
              <div className="w-50">
                <UserRsvp title={connectionComparison.connectionName} rsvp={connectionRsvp?.rsvp} />
              </div>
            )}
          </div>
          {buttons.length > 0 && (
            <div className="mt-3 d-grid gap-2">
              {buttons.map(({ key, buttonEl }) => (
                <div key={key}>{buttonEl}</div>
              ))}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

const EventCountCard: FC<{
  title: string;
  avatarProps: UserAvatarProps;
  count: number;
}> = ({ title, avatarProps, count }) => {
  return (
    <div>
      <div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ margin: '0 auto' }}
        >
          <div style={{ position: 'relative' }}>
            <UserAvatar {...avatarProps} size={60} />
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                margin: '0 auto',
                background: 'var(--color-teal)',
                borderRadius: 500,
                aspectRatio: 1,
                width: 24,
                position: 'absolute',
                bottom: -4,
                right: -4,
              }}
            >
              <strong>{count}</strong>
            </div>
          </div>
        </div>
      </div>
      <div className="fs-6 mt-2" style={{ opacity: 0.7 }}>
        <strong>{title}</strong>
      </div>
    </div>
  );
};

const OverviewSection: FC<{
  currentUserEventCount: number;
  connectionEventCount: number;
  currentUserEvents: (PublicFeedEvent | ScheduleDateDetailsEvent)[];
  connectionEvents: (PublicFeedEvent | ScheduleDateDetailsEvent)[];
  connectionName: string;
}> = ({
  currentUserEventCount,
  connectionEventCount,
  currentUserEvents,
  connectionEvents,
  connectionName,
}) => {
  const message = useMemo(() => {
    if (currentUserEventCount === 0 && connectionEventCount === 0) {
      return "Neither of you have RSVP'd to any events. Go to the calendar to see what's happening and add events to your calendar.";
    }

    const currentUserEventsText = currentUserEventCount
      ? `You've RSVP'd to ${currentUserEventCount} ${pluralize('event', currentUserEventCount)}`
      : `You haven't RSVP'd to any events`;
    const conjuctionWord =
      (currentUserEventCount && connectionEventCount) ||
      (!currentUserEventCount && !connectionEventCount)
        ? ', and'
        : ', but';
    const connectionEventsText = connectionEventCount
      ? `${connectionName} has RSVP'd to ${connectionEventCount} ${pluralize('event', connectionEventCount)}`
      : `${connectionName} hasn't RSVP'd to any events`;

    const additionalText =
      currentUserEventCount || connectionEventCount
        ? 'Below you can see their upcoming events and compare your plans.'
        : '';

    return `${currentUserEventsText}${conjuctionWord} ${connectionEventsText}. ${additionalText}`;
  }, [
    currentUserEventCount,
    connectionEventCount,
    currentUserEvents,
    connectionEvents,
    connectionName,
  ]);

  return <div className="mb-3">{message}</div>;
};

const SuggestedFriendsSection: FC<{}> = ({}) => {
  const { connectionsAreReady, suggestedConnections } = useUserConnectionsQuery();

  if (!connectionsAreReady) {
    return <Skeleton height={120} />;
  }

  if (!suggestedConnections.length) {
    return <></>;
  }

  return (
    <>
      <TitleToolbar text="Suggested Friends" size="sm" />
      <HorizontalScroll
        items={[
          <div className="d-flex gap-2 pt-2 pe-4">
            {suggestedConnections.map((suggestedConnection) => (
              <div key={suggestedConnection.connectionUserId}>
                <SuggestedConnectionCard connection={suggestedConnection} />
              </div>
            ))}
          </div>,
        ]}
      />
    </>
  );
};

export const UserConnectionComparisonScreen = createScreenComponent<
  { otherUserId: string },
  { autoAcceptKey?: string }
>(({ otherUserId, back, f7route }) => {
  const { track } = useAnalytics();
  const { currentUser } = useCurrentUser();
  const { connectionComparisonIsReady, connectionComparison } =
    useUserConnectionComparisonQuery(otherUserId);
  const { eventsAreReady, events } = useUserEventsQuery({
    ignoreLocation: true,
  });
  const { createAuthGateHandler } = useAuthGate();
  const { backlinkUrl } = useManagedHistory();

  const navigate = useNavigate();

  const isCurrentUser = currentUser?._id === otherUserId;

  const currentUserEvents = useMemo(() => {
    return [...(events?.going ?? []), ...(events?.maybe ?? []), ...(events?.interested ?? [])];
  }, [events]);

  const connectionEvents = useMemo(() => {
    return [
      ...(connectionComparison?.goingEvents ?? []),
      ...(connectionComparison?.maybeEvents ?? []),
      ...(connectionComparison?.interestedEvents ?? []),
    ];
  }, [connectionComparison]);

  const connectionEventCount = useMemo(() => {
    return (
      (connectionComparison?.goingEventCount ?? 0) +
      (connectionComparison?.maybeEventCount ?? 0) +
      (connectionComparison?.interestedEventCount ?? 0)
    );
  }, [connectionComparison]);

  const combinedEvents = useMemo(
    () => orderBy(uniqBy([...currentUserEvents, ...connectionEvents], '_id'), 'startDate', 'asc'),
    [currentUserEvents, connectionEvents],
  );

  const connectionRsvps = useMemo(() => {
    return Object.fromEntries(
      Object.entries(connectionComparison?.connectionRsvps ?? {}).map(([id, rsvp]) => [
        id,
        { rsvp },
      ]),
    ) as Record<string, { rsvp: EventRsvpOption | 'interested' }>;
  }, [connectionComparison]);

  const { isSendingConnectionRequest, sendConnectionRequestAsync } = useSendConnectionRequest();

  const handleConnect = () => {
    track('Connect with user', {
      userId: otherUserId,
    });

    createAuthGateHandler(
      async () => {
        await sendConnectionRequestAsync({
          toUserId: otherUserId,
          autoAcceptKey: f7route.query?.autoAcceptKey,
        });
      },
      {
        isUserOnboarding: true,
      },
    );
  };

  return (
    <Screen
      name="User Connection Comparison Screen"
      hideToolbar
      disableLocationPermissionGate
      headerProps={{
        back: currentUser?._id ? { to: `/manage/connections` } : undefined,
        title: <Screen.GagglTitle />,
      }}
    >
      <Screen.Content>
        <div className="mb-3 text-center fs-3">
          <strong>{`Upcoming ${capitalize(GATHERING_TERM_PLURAL)}`}</strong>
        </div>
        {connectionComparisonIsReady && eventsAreReady ? (
          <>
            {connectionComparison && (
              <>
                <div className="mb-3 text-center d-flex gap-3 w-100">
                  <div className={isCurrentUser ? 'w-100' : 'w-50'}>
                    <EventCountCard
                      title="You"
                      avatarProps={{
                        displayName: 'You',
                        avatarUrl: currentUser?.avatar,
                      }}
                      count={currentUserEvents.length}
                    />
                  </div>
                  {!isCurrentUser && (
                    <div className="w-50">
                      <EventCountCard
                        title={connectionComparison.connectionName}
                        avatarProps={{
                          displayName: connectionComparison.connectionName,
                          avatarUrl: connectionComparison.connectionAvatar,
                        }}
                        count={connectionEventCount}
                      />
                    </div>
                  )}
                </div>

                {!isCurrentUser && (
                  <div className="d-grid gap-2">
                    <DenverPride2026PrimaryCtaCard tagSlug="denver-queer-calendar" />

                    <OverviewSection
                      currentUserEventCount={currentUserEvents.length}
                      connectionEventCount={connectionEventCount}
                      currentUserEvents={currentUserEvents}
                      connectionEvents={connectionEvents}
                      connectionName={connectionComparison.connectionName}
                    />
                  </div>
                )}

                {isCurrentUser && (
                  <div>
                    <DenverPride2026PrimaryCtaCard
                      hideIfCompleted
                      className="mb-2"
                      tagSlug="denver-queer-calendar"
                    />

                    <div
                      className="p-3"
                      style={{ background: 'rgba(255,255,255,.2)', borderRadius: 8 }}
                    >
                      <div className="mb-2 text-center">{`Share your profile link with friends so that they can see your upcoming ${GATHERING_TERM_PLURAL} and compare your plans!`}</div>
                      <CopyText text={currentUser?.connectionInviteUrl} />
                    </div>

                    <SuggestedFriendsSection />

                    <TitleToolbar text="Your RSVPs" size="sm" />
                  </div>
                )}

                {connectionComparison._id ||
                connectionComparison.isCalendarPublic ||
                isCurrentUser ? (
                  <>
                    {connectionComparison.status === PublicUserConnectionStatus.PendingSent ? (
                      <EmptyState
                        title={`Connection Request Sent!`}
                        text={`You've sent a request to connect to ${connectionComparison.connectionName}. When they accept, you'll be able to see their upcoming ${GATHERING_TERM_PLURAL} and compare your plans.`}
                        button={{
                          startIcon: <FontAwesomeIcon name="plus" />,
                          text: 'Request sent!',
                          onClick: () => {},
                          disabled: true,
                        }}
                      />
                    ) : (
                      <>
                        {combinedEvents.map((event) => (
                          <EventComparisonCard
                            key={event._id}
                            event={event}
                            isCurrentUser={isCurrentUser}
                            connectionRsvp={connectionRsvps[event._id]}
                            connectionComparison={connectionComparison}
                          />
                        ))}
                      </>
                    )}

                    <div className="mt-3">
                      <EmptyState
                        title="Denver Queer Calendar"
                        text="There are lots of events coming up in Denver! Check out the calendar to see what's happening and add events to your calendar."
                        button={{
                          text: 'Explore events',
                          onClick: () => {
                            track('Click UCC Explore Events');
                            navigate('/noauth/series/denver-queer-calendar?tab=rsvpToEvents', {
                              state: {
                                back: `/`,
                              },
                            });
                          },
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <EmptyState
                      title={`This calendar is private`}
                      text={`${connectionComparison.connectionName} is not sharing their calendar publicly, but you can request access to see their upcoming ${GATHERING_TERM_PLURAL} and compare your plans!`}
                      button={{
                        startIcon: <FontAwesomeIcon name="paper-plane" />,
                        text: 'Request access',
                        onClick: handleConnect,
                        disabled: isSendingConnectionRequest,
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <Skeleton height={400} />
          </>
        )}
      </Screen.Content>

      <UserOnboardingGatePopup />
    </Screen>
  );
});
